import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import useAnalytics from '../analytics/useAnalytics';
import { EVT_LIBRARY_LEAD_VIEW } from '../analytics/events';
import useLibraries from './hooks/useLibraries';
import usePageLayout from './hooks/usePageLayout';
import sectionRegistry from './editable/sectionRegistry';
import { DEFAULT_LAYOUTS } from './editable/defaultLayout';

/**
 * Returns additional live-data props that a given section type needs.
 * These props are always injected from the real useLibraries() data source
 * regardless of what the DB-stored layout says, so dynamic content is never stale.
 *
 * priceTiers is intentionally excluded — it receives its tiers directly from
 * the page layout props, like every other static section.
 */
function dynamicPropsFor(type, liveData) {
  const { totalCount, uniqueCities, topLibraries, websiteLibraries, loading } = liveData;
  switch (type) {
    case 'hero':
      return { totalCount };
    case 'socialProof':
      return { totalCount, uniqueCities, loading };
    case 'websiteAddon':
      return { libraries: websiteLibraries, loading };
    case 'featuredLibraries':
      return { libraries: topLibraries, loading };
    default:
      return {};
  }
}

function JoinUsAsLibrary() {
  const { track } = useAnalytics();
  const tracked = useRef(false);

  const { totalCount, uniqueCities, topLibraries, websiteLibraries, loading } = useLibraries();
  const { layout, loading: layoutLoading } = usePageLayout('join-us-as-library');
  // Note: featuredLibraries from usePageLayout is intentionally unused here —
  // JoinUsAsLibrary injects live topLibraries dynamically via dynamicPropsFor().

  useEffect(() => {
    document.title = 'Join BookMyLibrary — List your library';
    if (!tracked.current) {
      tracked.current = true;
      track(EVT_LIBRARY_LEAD_VIEW);
    }
  }, [track]);

  // While the layout is fetching, render the client default so there is no flash or blank.
  const activeLayout = layoutLoading
    ? DEFAULT_LAYOUTS['join-us-as-library']
    : layout;

  const liveData = { totalCount, uniqueCities, topLibraries, websiteLibraries, loading };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {activeLayout
        .filter((section) => section.visible !== false)
        .map((section) => {
          const Cmp = sectionRegistry[section.type];
          if (!Cmp) return null;
          return (
            <Cmp
              key={section.id}
              {...(section.props || {})}
              {...dynamicPropsFor(section.type, liveData)}
            />
          );
        })}
    </Box>
  );
}

export default JoinUsAsLibrary;
